import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import * as _ from 'lodash-es';
import moment from 'moment';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as styles from './ControlBarInsights.module.scss';
import ControlBarInsightsUsersSelect from './ControlBarInsightsUsersSelect';
import PostInsights from './PostInsights';
import { PostPolicyAction, usePostPolicyCheck } from './postPolicyCheck';
import useCanDisplayNewPostHistoryView from './useCanDisplayNewPostHistoryView';
import { useRightPanel } from '../app/layout/RightPanel';
import useAppSelector from '../app/redux/useAppSelector';
import Avatar from '../components/Avatar';
import AvatarStack from '../components/AvatarStack';
import Button from '../components/Button';
import Caret from '../components/Caret';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import partitionAndGroupHistories from './history/partitionAndGroupHistories';
import { useUpdatePostOwner } from './sharing/postPermissionOperations';
import type { TableDocument } from '../editor/table/types';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated/gql';
import type { Post } from '../graphql/generated/graphql';
import {
  PostContributorSetStatus,
  PostContributorStatus,
} from '../graphql/generated/graphql';
import arrowUpRightIcon from '../icons/non-prefilled/16x16/arrowUpRight.svg';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';
import clockIcon from '../icons/non-prefilled/16x16/clock.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import hideIcon from '../icons/non-prefilled/16x16/hide.svg';
import readIcon from '../icons/non-prefilled/16x16/read.svg';
import teamIcon from '../icons/non-prefilled/16x16/team.svg';
import thanksIcon from '../icons/non-prefilled/16x16/thanks.svg';
import { useAllUsersMap, useGetUser } from '../user/useAllUsers';
import UserRow from '../user/UserRow';
import addTestId from '../utils/addTestId';
import { isFeatureEnabled } from '../utils/features';
import Link from '../utils/router/Link';
import historyPath from '../utils/url/historyPath';
import postPath from '../utils/url/postPath';
import userPath from '../utils/url/userPath';

gql(`
  fragment ControlBarUsers_user on User {
    id
    ...UserRow_user
  }
`);

interface MetaButtonProps {
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}
const MetaButton = React.forwardRef<HTMLDivElement, MetaButtonProps>(
  (
    {
      onClick,
      children,
      active,
      disabled,
      onMouseEnter,
      onMouseLeave,
      ...props
    },
    ref,
  ) => (
    <div ref={ref} {...props} className={styles.metaButtonWrapper}>
      <button
        type="button"
        disabled={disabled}
        className={classNames(styles.metaButton, {
          [styles.active]: active,
          [styles.disabled]: disabled,
        })}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
      </button>
    </div>
  ),
);

gql(`
  fragment ControlBarInsights_thanks on Thank {
    user {
      id
      ...useGetUser_user
    }
  }
`);

const CONTRIBUTION_FRAGMENT = gql(`
  fragment ControlBarInsights_contribution on Contribution {
    status
    user {
      id
      ...useGetUser_user
    }
  }
`);

const POST_FRAGMENT = gql(`
  fragment ControlBarInsights_post on Post {
    id
    owner {
      id
      ...UserRow_user
    }
    contributions(showHidden: true) {
      ...ControlBarInsights_contribution
    }
    thanks {
      ...ControlBarInsights_thanks
    }
    insertedAt
    archivedAt
    publishedAt
    lastHistory {
      id
      insertedAt
      versionFrom
      versionTo
    }
    histories {
      id
      ...partitionAndGroupHistories_history
    }
    ...usePostPolicyCheck_post
    ...historyPath_post
  }
`);

const THANK_POST_MUTATION = gql(/** @complexity 10 */ `
  mutation postThank($id: ID!) {
    post: thankPost(id: $id) {
      id
      thanks {
        ...ControlBarInsights_thanks
      }
    }
  }
`);

const UNTHANK_POST_MUTATION = gql(/** @complexity 10 */ `
  mutation postUnthank($id: ID!) {
    post: unthankPost(id: $id) {
      id
      thanks {
        ...ControlBarInsights_thanks
      }
    }
  }
`);

const CJK_LETTER_REGEX =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/gu;
const PUNCTUATION_REGEX = /\p{Punctuation}/gu;
const LETTER_REGEX = /[\p{L}\d]/gu;

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const calculateWordCount = (text: string) => {
  let nonPunctuationCJKLetterCount = 0;

  const nonCJKText = text.replace(CJK_LETTER_REGEX, (match) => {
    if (!match.match(PUNCTUATION_REGEX)) {
      nonPunctuationCJKLetterCount += 1;
    }
    return ' ';
  });

  const nonCJKWordCount = nonCJKText
    .split(/\s+/)
    .filter((part) => part.match(LETTER_REGEX)).length;

  return nonCJKWordCount + nonPunctuationCJKLetterCount;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const calculateCharacterCount = (text: string) =>
  [...text.replace(/\n/g, '')].length;

const processDelta = (
  ops: Delta['ops'],
): { text: string; embedCount: number } => {
  let embedCount = 0;
  let text = '';

  ops.forEach(({ insert }) => {
    if (typeof insert === 'string') {
      text += insert;
    } else if (typeof insert === 'object' && insert['code-embed']) {
      const processed = processDelta(insert['code-embed'] as Delta['ops']);
      text += processed.text;
      embedCount += processed.embedCount;
    } else if (typeof insert === 'object' && insert['table-embed']) {
      const { cells = {} } = insert['table-embed'] as TableDocument;

      Object.values(cells).forEach(({ content }) => {
        if (content) {
          const processed = processDelta(content);
          text += processed.text;
          embedCount += processed.embedCount;
        }
      });
    } else {
      embedCount += 1;
      text += ' ';
    }
  });

  return { text, embedCount };
};

interface PostCounts {
  word: number | null;
  character: number | null;
}

const usePostCounts = () => {
  const quill = useAppSelector((state) => state.editor.quill);
  const [postCounts, setPostCounts] = useState<PostCounts>({
    word: null,
    character: null,
  });

  useEffect(() => {
    if (!quill) return undefined;

    const updatePostCounts = (delta: Delta) => {
      const { text, embedCount } = processDelta(delta.ops);
      setPostCounts({
        word: calculateWordCount(text) + embedCount,
        character: calculateCharacterCount(text),
      });
    };

    updatePostCounts(quill.getContents());

    const textChangeHandler = _.debounce(
      (change: Delta, oldDelta: Delta) =>
        updatePostCounts(oldDelta.compose(change)),
      1000,
      { maxWait: 3000 },
    );
    quill.on(Quill.events.TEXT_CHANGE, textChangeHandler);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, textChangeHandler);
      textChangeHandler.cancel();
    };
  }, [quill]);

  return postCounts;
};
const SET_CONTRIBUTOR_STATUS_MUTATION = gql(/** @complexity 2 */ `
  mutation setContributorStatus($postId: ID!, $userId: ID!, $status: PostContributorSetStatus!) {
    setContributorStatus(postId: $postId, userId: $userId, status: $status)
  }`);

type InsightGroupItemProps = {
  title: string;
  value: React.ReactNode;
  valueTestId?: string;
} & ({ to?: never } | { to: string; onClick?: () => void });

const InsightGroupItem = ({
  title,
  value,
  valueTestId,
  ...props
}: InsightGroupItemProps) => {
  const inner = (
    <>
      <div className={styles.insightGroupItemTitle}>
        {title}

        {props.to && (
          <SVGIcon className={styles.insightGroupItemIcon} inline size={14}>
            {arrowUpRightIcon}
          </SVGIcon>
        )}
      </div>
      <div {...addTestId(valueTestId)} className={styles.insightGroupItemValue}>
        {value}
      </div>
    </>
  );

  if (props.to) {
    return (
      <Link
        className={styles.insightGroupItem}
        to={props.to}
        onClick={props.onClick}
      >
        {inner}
      </Link>
    );
  }

  return <div className={styles.insightGroupItem}>{inner}</div>;
};

const formatDate = (date: string) => {
  const dateMoment = moment(date);
  if (dateMoment.isSame(moment(), 'day')) {
    return `${dateMoment.format('h:mma')} today`;
  }
  return moment(date).format('MMM D, YYYY');
};

interface HistoryInsightGroupItemProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  close: () => void;
}

const HistoryInsightGroupItem = ({
  post,
  close,
}: HistoryInsightGroupItemProps) => {
  const canDisplayNewPostHistoryView = useCanDisplayNewPostHistoryView();

  if (!isFeatureEnabled('history_v2')) {
    if (!post.lastHistory) {
      return (
        <InsightGroupItem
          title="last edited"
          value={formatDate(post.insertedAt)}
        />
      );
    }

    return (
      <InsightGroupItem
        to={historyPath({
          post,
          versionFrom: post.lastHistory.versionFrom,
          versionTo: post.lastHistory.versionTo,
        })}
        onClick={close}
        title="last edited"
        value={
          <>
            {formatDate(post.lastHistory.insertedAt)}
            <SVGIcon className={styles.insightGroupItemIcon} inline>
              {clockIcon}
            </SVGIcon>
          </>
        }
      />
    );
  }

  const { accessibleHistories, restrictedHistories } =
    partitionAndGroupHistories(post.histories);
  const lastAccessibleHistory = accessibleHistories.at(0);
  const lastRestrictedHistory = restrictedHistories.at(0);

  if (canDisplayNewPostHistoryView) {
    if (lastAccessibleHistory) {
      return (
        <InsightGroupItem
          to={historyPath({
            post,
            versionFrom: lastAccessibleHistory.versionFrom,
            versionTo: lastAccessibleHistory.versionTo,
          })}
          onClick={close}
          title="last edited"
          value={
            <>
              {formatDate(lastAccessibleHistory.insertedAt)}
              <SVGIcon className={styles.insightGroupItemIcon} inline>
                {clockIcon}
              </SVGIcon>
            </>
          }
        />
      );
    }

    if (lastRestrictedHistory) {
      return (
        <InsightGroupItem
          to={postPath(post, { histories: '' })}
          onClick={close}
          title="last edited"
          value={
            <>
              {formatDate(lastRestrictedHistory.insertedAt)}
              <SVGIcon className={styles.insightGroupItemIcon} inline>
                {clockIcon}
              </SVGIcon>
            </>
          }
        />
      );
    }
  }

  const insertedAt =
    lastAccessibleHistory?.insertedAt ??
    lastRestrictedHistory?.insertedAt ??
    post.insertedAt;

  return (
    <InsightGroupItem title="last edited" value={formatDate(insertedAt)} />
  );
};

interface ControlBarInsightsProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  close: () => void;
}

const ControlBarInsights = ({ post, close }: ControlBarInsightsProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const postCounts = usePostCounts();
  const allUsersMap = useAllUsersMap();
  const [rightContent, setRightContent] = useState<{
    type: 'owner' | 'contributors' | 'thankers';
    transformPosition: string;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const ownerRef = useRef<HTMLDivElement>(null);
  const contributorsRef = useRef<HTMLDivElement>(null);
  const thankersRef = useRef<HTMLDivElement>(null);
  const getUser = useGetUser();

  const thankers = post.thanks.map((thank) => getUser(thank.user));

  const updateOwner = useUpdatePostOwner();

  const [thankMutation] = useMutation(THANK_POST_MUTATION);
  const [unthankMutation] = useMutation(UNTHANK_POST_MUTATION);
  const [setContributorStatusMutation] = useMutation(
    SET_CONTRIBUTOR_STATUS_MUTATION,
  );

  const isThanker = post.thanks.some((t) => t.user.id === currentUser.id);

  const thank = () => {
    thankMutation({
      variables: { id: post.id },
      optimisticResponse: {
        __typename: 'RootMutationType',
        post: {
          ...post,
          thanks: [...post.thanks, { __typename: 'Thank', user: currentUser }],
        },
      },
    });
  };

  const unthank = () => {
    unthankMutation({
      variables: { id: post.id },
      optimisticResponse: {
        __typename: 'RootMutationType',
        post: {
          ...post,
          thanks: post.thanks.filter((t) => t.user.id !== currentUser.id),
        },
      },
    });
  };

  const setContributorStatus = (
    userId: string,
    status: PostContributorSetStatus,
  ) => {
    setContributorStatusMutation({
      variables: { postId: post.id, userId, status },
      optimisticResponse: {
        __typename: 'RootMutationType',
        setContributorStatus: (() => {
          if (status === PostContributorSetStatus.Shown) {
            return contributionUserIds.has(userId)
              ? PostContributorStatus.Shown
              : PostContributorStatus.ManualAndShown;
          }
          return PostContributorStatus.Hidden;
        })(),
      },
      update: (cache) => {
        cache.modify<Post>({
          id: cache.identify(post),
          fields: {
            contributions(existingContributions, { readField }) {
              const user = allUsersMap[userId];
              if (!user) {
                return existingContributions;
              }

              const getUserIdFromContribution = (
                contribution: (typeof existingContributions)[number],
              ) => {
                const cUser = readField('user', contribution) as {
                  __ref: string;
                } | null;
                return cUser ? readField('id', cUser) : null;
              };

              const existingContribution = existingContributions.find(
                (contribution) =>
                  getUserIdFromContribution(contribution) === userId,
              );

              if (!existingContribution) {
                const newContributionRef = cache.writeFragment({
                  data: {
                    status: PostContributorStatus.ManualAndShown,
                    user: { importedFrom: null, ...user },
                    __typename: 'Contribution',
                  },
                  fragment: CONTRIBUTION_FRAGMENT,
                  fragmentName: 'ControlBarInsights_contribution',
                });
                return [...existingContributions, newContributionRef];
              }

              if (
                readField('status', existingContribution) ===
                PostContributorStatus.ManualAndShown
              ) {
                return existingContributions.filter(
                  (contribution) =>
                    getUserIdFromContribution(contribution) !== userId,
                );
              }

              const updatedContributionRef = cache.writeFragment({
                data: {
                  status:
                    status === PostContributorSetStatus.Shown
                      ? PostContributorStatus.Shown
                      : PostContributorStatus.Hidden,
                  user: { importedFrom: null, ...user },
                  __typename: 'Contribution',
                },
                fragment: CONTRIBUTION_FRAGMENT,
                fragmentName: 'ControlBarInsights_contribution',
              });

              return existingContributions.map((contribution) =>
                getUserIdFromContribution(contribution) === userId
                  ? updatedContributionRef
                  : contribution,
              );
            },
          },
        });
      },
    });
  };

  const getSetRightContent =
    (content: 'owner' | 'contributors' | 'thankers') =>
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (rightContent?.type === content) {
        setRightContent(null);
        return;
      }

      const activeSectionOffset = (
        {
          owner: ownerRef,
          contributors: contributorsRef,
          thankers: thankersRef,
        } as const
      )[content].current?.offsetTop;

      if (activeSectionOffset && containerRef.current) {
        setRightContent({
          type: content,
          transformPosition: `translateY(
            min(
              calc(
                ${activeSectionOffset}px + 100% - ${containerRef.current.clientHeight}px - 18px
                )
              , 0px
            )
          )`,
        });
      }
    };

  const { contributions, contributionUserIds } = useMemo(() => {
    const filteredContributions = post.contributions.filter(
      (c) => c.user.id !== post.owner.id,
    );

    const userIds = new Set(filteredContributions.map((c) => c.user.id));

    return {
      contributions: filteredContributions,
      contributionUserIds: userIds,
    };
  }, [post.contributions, post.owner.id]);

  const suggestedOwnersSet = useMemo(
    () => new Set(contributions.slice(0, 5).map((c) => c.user.id)),
    [contributions],
  );

  const postPolicyCheck = usePostPolicyCheck();
  const canEditPost = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    currentUser,
  );

  const shownContributors = contributions.filter(
    (c) => c.status !== PostContributorStatus.Hidden,
  );

  const { openRightPanel } = useRightPanel();

  return (
    <>
      <div
        className={styles.container}
        onClick={() => setRightContent(null)}
        ref={containerRef}
      >
        <div className={styles.section} ref={ownerRef}>
          <div className={styles.title}>
            owner
            <MetaButton
              active={rightContent?.type === 'owner'}
              disabled={!canEditPost}
              onClick={getSetRightContent('owner')}
            >
              <SVGIcon size={14}>{editIcon}</SVGIcon>
            </MetaButton>
          </div>

          <Link
            className={styles.userRowLink}
            to={userPath(getUser(post.owner))}
            newTab
          >
            <UserRow user={post.owner} avatarSize={32} />
          </Link>
        </div>
        <div className={styles.section} ref={contributorsRef}>
          <div className={styles.title}>
            contributors
            <MetaButton
              disabled={!canEditPost}
              active={rightContent?.type === 'contributors'}
              onClick={getSetRightContent('contributors')}
            >
              <Caret direction="right" size={12} />
            </MetaButton>
          </div>
          {shownContributors.length > 0 ? (
            <AvatarStack
              size={32}
              items={shownContributors.map((c) => getUser(c.user))}
              limit={9}
              countOverflowVariant="circle"
              spacing="tight"
            >
              {(user, options) => (
                <Link to={userPath(user)} newTab>
                  <Avatar user={user} size={options.size} />
                </Link>
              )}
            </AvatarStack>
          ) : (
            <button
              onClick={getSetRightContent('contributors')}
              type="button"
              className={styles.actionButton}
              disabled
            >
              <SVGIcon size={16}>{teamIcon}</SVGIcon>
            </button>
          )}
        </div>
        <div className={styles.section} ref={thankersRef}>
          <div className={styles.title}>
            thankers
            <MetaButton
              active={rightContent?.type === 'thankers'}
              disabled={thankers.length === 0}
              onClick={getSetRightContent('thankers')}
            >
              <Caret direction="right" size={12} />
            </MetaButton>
          </div>
          <div className={styles.thankers}>
            <AvatarStack
              size={32}
              items={thankers}
              limit={8}
              countOverflowVariant="circle"
              expandable
              spacing="tight"
            >
              {(user, options) => (
                <Link to={userPath(user)} newTab>
                  <Avatar user={user} size={options.size} />
                </Link>
              )}
            </AvatarStack>
            {!isThanker && (
              <Tooltip
                content={'Give thanks\nto the owner'}
                placement="top"
                trigger="hover"
              >
                <button
                  aria-label="Thank"
                  onClick={thank}
                  type="button"
                  className={styles.actionButton}
                >
                  <SVGIcon size={16}>{thanksIcon}</SVGIcon>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <div
          className={classNames(styles.insightsSection, styles.insightGroup)}
        >
          {!!post.archivedAt && (
            <InsightGroupItem
              title="date archived"
              value={formatDate(post.archivedAt)}
            />
          )}
          {!post.archivedAt && !!post.publishedAt && (
            <InsightGroupItem
              title="date published"
              value={formatDate(post.publishedAt)}
            />
          )}
          {!post.archivedAt && !post.publishedAt && (
            <InsightGroupItem
              title="date created"
              value={formatDate(post.insertedAt)}
            />
          )}
          <HistoryInsightGroupItem post={post} close={close} />
        </div>
        <div className={classNames(styles.section, styles.insightGroup)}>
          <InsightGroupItem
            title="word count"
            value={postCounts.word?.toLocaleString()}
            valueTestId="controlBarInsightsWordCount"
          />
          <InsightGroupItem
            title="character count"
            value={postCounts.character?.toLocaleString()}
            valueTestId="controlBarInsightsCharacterCount"
          />
        </div>
        <div className={styles.section}>
          <Button
            variant="secondary"
            className={styles.openInsightsButton}
            onClick={() => {
              openRightPanel(<PostInsights id={post.id} />);
              close();
            }}
          >
            <span>Open insights</span>
            <SVGIcon size={14}>{arrowUpRightIcon}</SVGIcon>
          </Button>
        </div>
      </div>
      {rightContent && (
        <div
          className={styles.rightContent}
          onClick={(e) => e.stopPropagation()}
          style={{ transform: rightContent.transformPosition }}
        >
          {rightContent.type === 'owner' && (
            <div className={styles.usersContainer}>
              <ControlBarInsightsUsersSelect
                autoFocus
                keepOpen
                placeholder="Pick a new owner..."
                onSelect={async (user) => {
                  setRightContent(null);
                  await updateOwner(post, user);
                }}
                filter={(user, inputValue) => {
                  return (
                    post.owner.id !== user.id &&
                    (!!inputValue ||
                      suggestedOwnersSet.size === 0 ||
                      suggestedOwnersSet.has(user.id))
                  );
                }}
              />
            </div>
          )}
          {rightContent.type === 'contributors' && (
            <div className={styles.usersContainer}>
              {canEditPost && (
                <ControlBarInsightsUsersSelect
                  placeholder="Add new contributors..."
                  autoFocus={contributions.length === 0}
                  keepOpen={contributions.length === 0}
                  onSelect={(user) => {
                    setContributorStatus(
                      user.id,
                      PostContributorSetStatus.Shown,
                    );
                  }}
                  filter={(user) =>
                    user.id !== post.owner.id &&
                    !contributionUserIds.has(user.id)
                  }
                />
              )}
              <div className={styles.usersContent}>
                {contributions.length > 0 && (
                  <div
                    className={classNames(styles.users, {
                      [styles.underSelect]: canEditPost,
                    })}
                  >
                    {contributions.map((contribution) => {
                      return (
                        <div key={contribution.user.id} className={styles.user}>
                          <Link
                            to={userPath(getUser(contribution.user))}
                            newTab
                            key={contribution.user.id}
                            className={classNames(
                              styles.contributorRow,
                              styles.userRowLink,
                            )}
                          >
                            <UserRow user={contribution.user} />
                          </Link>
                          <div className={styles.userMeta}>
                            <MetaButton
                              onClick={() => {
                                setContributorStatus(
                                  contribution.user.id,
                                  contribution.status ===
                                    PostContributorStatus.Hidden
                                    ? PostContributorSetStatus.Shown
                                    : PostContributorSetStatus.Hidden,
                                );
                              }}
                            >
                              {contribution.status ===
                                PostContributorStatus.Shown && (
                                <SVGIcon size={16}>{readIcon}</SVGIcon>
                              )}
                              {contribution.status ===
                                PostContributorStatus.Hidden && (
                                <SVGIcon size={16}>{hideIcon}</SVGIcon>
                              )}
                              {contribution.status ===
                                PostContributorStatus.ManualAndShown && (
                                <SVGIcon size={16}>{clearIcon}</SVGIcon>
                              )}
                            </MetaButton>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          {rightContent.type === 'thankers' && thankers.length > 0 && (
            <div className={styles.usersContainer}>
              <div className={styles.usersContent}>
                <div className={styles.users}>
                  {thankers.map((thanker) => (
                    <div key={thanker.id} className={styles.user}>
                      <Link
                        to={userPath(thanker)}
                        newTab
                        key={thanker.id}
                        className={styles.userRowLink}
                      >
                        <UserRow user={thanker} />
                      </Link>
                      {thanker.id === currentUser.id && (
                        <div className={styles.userMeta}>
                          <Tooltip
                            content="Remove your thanks"
                            placement="top"
                            trigger="hover"
                            asChild
                          >
                            <MetaButton onClick={() => unthank()}>
                              <SVGIcon size={16}>{clearIcon}</SVGIcon>
                            </MetaButton>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ControlBarInsights;
